export const LANDING_CORE_MAX_BYTES = 800 * 1024;
export const LANDING_REFLECTIONS_VIZ_MAX_BYTES = 2 * 1024 * 1024;
/** Velocity is ONE band-integrated field, not a per-frequency cube, so it
 *  lands around 150 KB. The cap is deliberately far above that: its job is to
 *  catch a regression that starts shipping a cube here, not to police normal
 *  variation. */
export const LANDING_VELOCITY_VIZ_MAX_BYTES = 2 * 1024 * 1024;
export const LANDING_VOLUMETRIC_VIZ_WARN_BYTES = 15 * 1024 * 1024;
export const LANDING_TOTAL_MAX_BYTES = 25 * 1024 * 1024;

export type LandingSnapshotVizLayer = 'volume' | 'raytrace' | 'velocity';

export interface LandingSnapshotManifestEntry {
  templateId: string;
  variant: 'bare' | 'treated';
  hash: string;
  coreUrl: string;
  vizUrl: string;
  coreBytes: number;
  vizBytes: number;
  layers: LandingSnapshotVizLayer[];
}

export interface LandingSnapshotManifest {
  generatedAt: string;
  registryHash: string;
  entries: LandingSnapshotManifestEntry[];
}

export interface LandingBundleSizeGateInput {
  templateId: string;
  coreBytes: number;
  vizBytes: number;
  layers: LandingSnapshotVizLayer[];
}

export interface LandingBundleSizeGateResult {
  warnings: string[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function parseLayers(raw: unknown, path: string): LandingSnapshotVizLayer[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error(`${path}: non-empty array of volume|raytrace|velocity`);
  }
  return raw.map((layer, i) => {
    if (layer !== 'volume' && layer !== 'raytrace' && layer !== 'velocity') {
      throw new Error(`${path}[${i}]: expected volume | raytrace | velocity`);
    }
    return layer;
  });
}

function parseEntry(raw: unknown, path: string): LandingSnapshotManifestEntry {
  if (!isRecord(raw)) throw new Error(`${path}: expected object`);
  const templateId = raw.templateId;
  const variant = raw.variant;
  const hash = raw.hash;
  const coreUrl = raw.coreUrl;
  const vizUrl = raw.vizUrl;
  const coreBytes = raw.coreBytes;
  const vizBytes = raw.vizBytes;
  if (typeof templateId !== 'string' || !templateId) throw new Error(`${path}.templateId: required string`);
  if (variant !== 'bare' && variant !== 'treated') throw new Error(`${path}.variant: bare | treated`);
  if (typeof hash !== 'string' || !hash) throw new Error(`${path}.hash: required string`);
  if (typeof coreUrl !== 'string' || !coreUrl.endsWith('.core.json')) {
    throw new Error(`${path}.coreUrl: *.core.json`);
  }
  if (typeof vizUrl !== 'string' || !vizUrl.endsWith('.viz.json')) {
    throw new Error(`${path}.vizUrl: *.viz.json`);
  }
  if (typeof coreBytes !== 'number' || !Number.isFinite(coreBytes) || coreBytes < 0) {
    throw new Error(`${path}.coreBytes: non-negative number`);
  }
  if (typeof vizBytes !== 'number' || !Number.isFinite(vizBytes) || vizBytes < 0) {
    throw new Error(`${path}.vizBytes: non-negative number`);
  }
  return {
    templateId,
    variant,
    hash,
    coreUrl,
    vizUrl,
    coreBytes,
    vizBytes,
    layers: parseLayers(raw.layers, `${path}.layers`),
  };
}

export function parseLandingSnapshotManifest(raw: unknown): LandingSnapshotManifest {
  if (!isRecord(raw)) throw new Error('manifest: expected object');
  const generatedAt = raw.generatedAt;
  const registryHash = raw.registryHash;
  const entries = raw.entries;
  if (typeof generatedAt !== 'string' || !generatedAt) {
    throw new Error('generatedAt: required ISO-8601 string');
  }
  if (typeof registryHash !== 'string' || !registryHash) {
    throw new Error('registryHash: required string');
  }
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new Error('entries: non-empty array');
  }
  return {
    generatedAt,
    registryHash,
    entries: entries.map((e, i) => parseEntry(e, `entries[${i}]`)),
  };
}

export function assertLandingBundleSizeGates(
  entries: LandingBundleSizeGateInput[],
): LandingBundleSizeGateResult {
  const warnings: string[] = [];
  let total = 0;
  for (const entry of entries) {
    total += entry.coreBytes + entry.vizBytes;
    if (entry.coreBytes > LANDING_CORE_MAX_BYTES) {
      throw new Error(
        `${entry.templateId} core.json is ${entry.coreBytes} bytes (max ${LANDING_CORE_MAX_BYTES}) — investigate chart bloat`,
      );
    }
    // Layers that are small by construction get a HARD cap; `volume` is a
    // frequency cube and only warns, because shrinking it is an ongoing goal
    // rather than a thing a build should refuse to ship over.
    const soleLayer = entry.layers.length === 1 ? entry.layers[0] : null;
    const soleCap = soleLayer === 'raytrace'
      ? LANDING_REFLECTIONS_VIZ_MAX_BYTES
      : soleLayer === 'velocity'
        ? LANDING_VELOCITY_VIZ_MAX_BYTES
        : null;
    if (soleCap != null && entry.vizBytes > soleCap) {
      throw new Error(
        `${entry.templateId} ${soleLayer} viz.json is ${entry.vizBytes} bytes (max ${soleCap})`,
      );
    }
    if (entry.layers.includes('volume') && entry.vizBytes > LANDING_VOLUMETRIC_VIZ_WARN_BYTES) {
      warnings.push(
        `${entry.templateId} volumetric viz.json is ${entry.vizBytes} bytes (warn at ${LANDING_VOLUMETRIC_VIZ_WARN_BYTES}; goal is shrink over time)`,
      );
    }
  }
  if (total > LANDING_TOTAL_MAX_BYTES) {
    throw new Error(
      `total landing bundle set is ${total} bytes (max ${LANDING_TOTAL_MAX_BYTES}) — slim the set`,
    );
  }
  return { warnings };
}
